import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import CloseIcon from "@mui/icons-material/Close";
import DescriptionRoundedIcon from "@mui/icons-material/DescriptionRounded";
import HourglassEmptyRoundedIcon from "@mui/icons-material/HourglassEmptyRounded";
import {
  AppBar,
  Box,
  Button,
  Card,
  Dialog,
  Toolbar,
  Typography,
} from "@mui/material";
import { useState } from "react";
import { FORM_URL } from "../../config/config";

interface FormStatusSectionProps {
  submission: any;
  onCloseForm?: () => void;
  authToken: string;
}

export const FormStatusSection: React.FC<FormStatusSectionProps> = ({
  submission,
  onCloseForm,
  authToken,
}) => {
  const [open, setOpen] = useState(false);

  const getStatus = () => {
    if (!submission) return "NOT_STARTED";
    if (submission.json && !submission.finishedAt) return "IN_PROGRESS";
    if (submission.json && submission.finishedAt) return "COMPLETED";
    return "NOT_STARTED";
  };

  const status = getStatus();

  const statusConfig = {
    NOT_STARTED: {
      label: "Aún no has iniciado tu formulario",
      color: "error.main",
      icon: <HourglassEmptyRoundedIcon sx={{ color: "error.main" }} />,
    },
    IN_PROGRESS: {
      label: "Formulario en progreso",
      color: "warning.main",
      icon: <DescriptionRoundedIcon sx={{ color: "warning.main" }} />,
    },
    COMPLETED: {
      label: "Formulario completado 🎉",
      color: "success.main",
      icon: <CheckCircleRoundedIcon sx={{ color: "success.main" }} />,
    },
  }[status];

  const handleClose = () => {
    setOpen(false);
    if (onCloseForm) onCloseForm();
  };

  return (
    <>
      <Card
        elevation={0}
        sx={{
          borderRadius: 4,
          p: 3,
          mb: 4,
          background: "linear-gradient(135deg, #ffffff 0%, #f7f9fc 100%)",
          border: "1px solid rgba(0,0,0,0.05)",
          boxShadow: "0 4px 18px rgba(0,0,0,0.05)",
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          alignItems: { xs: "flex-start", sm: "center" },
          justifyContent: "space-between",
          gap: 2,
        }}
      >
        <Box display="flex" alignItems="center" gap={2}>
          <Box
            sx={{
              width: 50,
              height: 50,
              borderRadius: "50%",
              bgcolor: "rgba(0,0,0,0.05)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {statusConfig.icon}
          </Box>
          <Box>
            <Typography
              variant="subtitle1"
              fontWeight={700}
              color="text.primary"
            >
              Estado del formulario
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: statusConfig.color, fontWeight: 600 }}
            >
              {statusConfig.label}
            </Typography>
          </Box>
        </Box>

        {status !== "COMPLETED" && (
          <Button
            variant="contained"
            color="secondary"
            disableElevation
            onClick={() => setOpen(true)}
            sx={{
              minWidth: 160,
              fontWeight: 600,
              borderRadius: 2,
            }}
          >
            {"Abrir formulario"}
          </Button>
        )}
      </Card>

      <Dialog fullScreen open={open} onClose={handleClose}>
        <AppBar
          elevation={0}
          sx={{
            position: "relative",
            background:
              "linear-gradient(90deg, #0B1E6E 0%, #123BFF 60%, #0066FF 100%)",
            color: "white",
            boxShadow: "0 6px 18px rgba(0,0,0,0.15)",
          }}
        >
          <Toolbar
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              py: 1.2,
              px: { xs: 2, sm: 4 },
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <Box>
                <Typography
                  variant="h6"
                  fontWeight={700}
                  sx={{
                    letterSpacing: 0.5,
                    lineHeight: 1.2,
                    fontFamily: "Montserrat, sans-serif",
                  }}
                >
                  Formulario DS-160
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    color: "rgba(255,255,255,0.8)",
                    letterSpacing: 0.3,
                    display: "block",
                    fontSize: "0.8rem",
                  }}
                >
                  Registro de solicitud de visa
                </Typography>
              </Box>
            </Box>
            <Box
              sx={{
                width: 42,
                height: 42,
                borderRadius: "50%",
                bgcolor: "rgba(255,255,255,0.15)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                boxShadow: "0 0 12px rgba(255,255,255,0.25)",
              }}
            >
              <CloseIcon
                onClick={handleClose}
                sx={{
                  cursor: "pointer",
                  fontSize: 22,
                  transition: "0.2s",
                  "&:hover": { color: "#FFD700", transform: "scale(1.15)" },
                }}
              />
            </Box>
          </Toolbar>
        </AppBar>

        <Box sx={{ flex: 1, bgcolor: "#f5f5f5", height: "100%", p: 0 }}>
          {FORM_URL ? (
            <iframe
              src={`${FORM_URL}?token=${authToken}`}
              style={{
                border: "none",
                width: "100%",
                height: "100%",
              }}
              allowFullScreen
            />
          ) : (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: "100%",
                flexDirection: "column",
                color: "text.secondary",
              }}
            >
              <DescriptionRoundedIcon
                sx={{ fontSize: 60, mb: 2, opacity: 0.4 }}
              />
              <Typography variant="h6">
                No hay un formulario asignado todavía
              </Typography>
            </Box>
          )}
        </Box>
      </Dialog>
    </>
  );
};
